import { useIntl } from 'umi';

import { fetchUrlCollections, updateUrlCollections } from '@home/services/urlCollection';
import { restoreDefaultShortcutItem } from '@home/utils/urlCollection';
import {
  getUrlCollectionPrimaryEntityType,
  parseUrlCollectionData,
  navigateUrlCollectionUrl,
  openUrlCollectionExternalUrl,
  reindexUrlCollectionItems,
} from '@home/utils/urlCollectionData';

import { logUrlCollectionButtonClick, showShortcutNotification, URL_COLLECTION_LOG_CONFIG } from './utils';
import { getShortcutTargetUrl } from '../utils/shortcutRules';

import type { IUrlCollectionItem } from '@home/types/urlCollection';

interface IUseShortcutCollectionReturn {
  loadShortcuts: () => Promise<IUrlCollectionItem[]>;
  saveShortcuts: (nextItems: IUrlCollectionItem[]) => Promise<IUrlCollectionItem[] | null>;
  handleShortcutClick: (item: IUrlCollectionItem) => void;
}

const hydrateShortcutEntityDetails = async (items: IUrlCollectionItem[]): Promise<IUrlCollectionItem[]> => {
  const tasks = items.map(async (item) => {
    if (!item.entity_id || !item.is_default) {
      return item;
    }

    const currentData = parseUrlCollectionData(item.data);
    const defaultShortcutPatch = await restoreDefaultShortcutItem({
      system: item.system,
      url: item.url,
      name: item.name,
      data: item.data,
      page_id: item.page_id,
      is_default: item.is_default,
      entity_type: item.entity_type,
      entity_id: item.entity_id,
      entity_name: item.entity_name || currentData.entity_name || '',
      entity_cover: currentData.entity_cover || item.cover,
    });
    const nextData = parseUrlCollectionData(defaultShortcutPatch.data || item.data);
    const entityType = getUrlCollectionPrimaryEntityType(nextData.entity_type || item.entity_type);
    const entityId = nextData.entity_id || item.entity_id;
    const entityCover = nextData.entity_cover || item.cover;

    return {
      ...item,
      ...defaultShortcutPatch,
      cover: entityCover || item.cover,
      entity_type: entityType,
      entity_id: entityId,
      data: defaultShortcutPatch.data || item.data,
    };
  });

  const results = await Promise.allSettled(tasks);

  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    }

    return items[index];
  });
};

const useShortcutCollection = (): IUseShortcutCollectionReturn => {
  const { formatMessage } = useIntl();

  const loadShortcuts = async (): Promise<IUrlCollectionItem[]> => {
    try {
      const response = await fetchUrlCollections({});
      const shortcuts = Array.isArray(response?.shortcuts) ? response.shortcuts : [];
      const nextShortcuts = await hydrateShortcutEntityDetails(shortcuts);

      return nextShortcuts;
    } catch (error) {
      return [];
    }
  };

  const saveShortcuts = async (nextItems: IUrlCollectionItem[]): Promise<IUrlCollectionItem[] | null> => {
    try {
      const requestItems = reindexUrlCollectionItems(nextItems);
      const response = await updateUrlCollections({
        shortcuts: requestItems,
      });
      const shortcuts = Array.isArray(response?.shortcuts) ? response.shortcuts : requestItems;
      const nextShortcuts = await hydrateShortcutEntityDetails(shortcuts);

      showShortcutNotification(
        'success',
        formatMessage({
          id: 'dbh_url_collection_save_success',
          defaultMessage: 'Saved successfully!',
        }),
      );
      return nextShortcuts;
    } catch (error) {
      showShortcutNotification(
        'error',
        formatMessage({
          id: 'dbh_url_collection_save_failed',
          defaultMessage: 'Failed to save shortcuts.',
        }),
      );
      return null;
    }
  };

  const handleShortcutClick = (item: IUrlCollectionItem): void => {
    const targetUrl = getShortcutTargetUrl(item);

    logUrlCollectionButtonClick(URL_COLLECTION_LOG_CONFIG.clickShortcut, {
      id: item.id,
      name: item.name,
      url: targetUrl,
      system: item.system,
    });

    if (navigateUrlCollectionUrl(targetUrl)) {
      return;
    }

    openUrlCollectionExternalUrl(targetUrl);
  };

  return {
    loadShortcuts,
    saveShortcuts,
    handleShortcutClick,
  };
};

export default useShortcutCollection;
